import { useState, useEffect } from "react";
import {
  Settings,
  BookOpen,
  ClipboardCheck,
  DollarSign,
  Palette,
  Save,
  CreditCard,
  Building2,
  Landmark,
  Upload,
  Video,
  Link2,
  Shield,
  CheckCircle2,
  AlertCircle,
  Plus,
  Trash2,
  X,
  User,
  Star,
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Select } from "@/components/admin/ui/Select";
import { Toggle } from "@/components/admin/ui/Toggle";
import { Card } from "@/components/admin/ui/Card";
import { useToast } from "@/components/admin/ui/Toast";

export function SettingsPanel() {
  const [activeTab, setActiveTab] = useState<
    "general" | "course" | "evaluation" | "monetization" | "design" | "zoom" | "avatars" | "authentication"
  >("general");

  interface AvatarPreset {
    id: string | number;
    url: string;
    is_default: boolean;
  }
  const [avatarGallery, setAvatarGallery] = useState<AvatarPreset[]>([]);

  // General Settings States
  const [enableMarketplace, setEnableMarketplace] = useState(true);
  const [publicProfile, setPublicProfile] = useState(true);
  const [coursesPerPage, setCoursesPerPage] = useState("20");
  const [customDashboardUrl, setCustomDashboardUrl] = useState("/dashboard");
  
  // New Instructor Control Settings
  const [becomeInstructorButton, setBecomeInstructorButton] = useState(true);
  const [allowPublishCourses, setAllowPublishCourses] = useState(true);
  const [allowTrashCourses, setAllowTrashCourses] = useState(true);
  const [allowChangeAuthor, setAllowChangeAuthor] = useState(false);
  const [allowResetProgress, setAllowResetProgress] = useState(true);

  // Course Settings States
  const [enableCodeEditor, setEnableCodeEditor] = useState(true);
  const [videoAutoPlay, setVideoAutoPlay] = useState(false);

  // Preferred Video Sources
  const [videoSourceHtml5, setVideoSourceHtml5] = useState(true);
  const [videoSourceExternal, setVideoSourceExternal] = useState(true);
  const [videoSourceYoutube, setVideoSourceYoutube] = useState(true);
  const [videoSourceVimeo, setVideoSourceVimeo] = useState(true);
  const [videoSourceEmbedded, setVideoSourceEmbedded] = useState(true);
  const [videoSourceShortcode, setVideoSourceShortcode] = useState(true);

  // Tutor Player toggles
  const [tutorPlayerYoutube, setTutorPlayerYoutube] = useState(false);
  const [tutorPlayerVimeo, setTutorPlayerVimeo] = useState(false);

  const [courseRetake, setCourseRetake] = useState(true);
  const [lessonSidebarToggling, setLessonSidebarToggling] = useState(true);
  const [courseCompletionMode, setCourseCompletionMode] = useState<"strict" | "flexible">("strict");
  const [maxVideoUploadSize, setMaxVideoUploadSize] = useState("512");

  // Evaluation Settings States
  const [enableQuizTimer, setEnableQuizTimer] = useState(true);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  const [enableAssignmentUpload, setEnableAssignmentUpload] = useState(true);
  const [maxAssignmentFileSize, setMaxAssignmentFileSize] = useState("128");
  const [passingGradeDefault, setPassingGradeDefault] = useState("70");

  // Monetization Settings States
  const [paymentEngine, setPaymentEngine] = useState("woocommerce");
  const [earningEnabled, setEarningEnabled] = useState(true);
  const [adminCommission, setAdminCommission] = useState("20");
  const [payoutPaypal, setPayoutPaypal] = useState(true);
  const [payoutBank, setPayoutBank] = useState(false);
  const [payoutStripe, setPayoutStripe] = useState(true);

  // Sub-tabs & modal control states
  const [activeMonetizationSubTab, setActiveMonetizationSubTab] = useState<"payment-methods" | "subscriptions" | "taxes" | "checkout" | "referrals">("payment-methods");
  const [expandedGateway, setExpandedGateway] = useState<string | null>(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualTitle, setManualTitle] = useState("");
  const [manualIconUrl, setManualIconUrl] = useState("");
  const [manualInstructions, setManualInstructions] = useState("");

  // Subscriptions Settings States
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlanIdx, setEditingPlanIdx] = useState<number | null>(null);
  const [planForm, setPlanForm] = useState({
    name: "",
    tier: "pro",
    period: "monthly",
    regularPrice: "",
    salePrice: "",
    status: "active",
  });

  const handleSavePlan = () => {
    if (!planForm.name || !planForm.regularPrice) {
      showToast("Plan Name and Regular Price are required.", "error");
      return;
    }

    if (editingPlanIdx !== null) {
      setSubscriptionPlans(prev => {
        const next = [...prev];
        next[editingPlanIdx] = planForm;
        return next;
      });
      showToast("Plan updated.", "success");
    } else {
      setSubscriptionPlans(prev => [...prev, planForm]);
      showToast("Plan added.", "success");
    }
    setShowPlanForm(false);
    setEditingPlanIdx(null);
  };

  const handleEditPlan = (idx: number) => {
    setPlanForm(subscriptionPlans[idx]);
    setEditingPlanIdx(idx);
    setShowPlanForm(true);
  };

  const handleDeletePlan = (idx: number) => {
    if (confirm("Are you sure you want to delete this plan?")) {
      setSubscriptionPlans(prev => prev.filter((_, i) => i !== idx));
      showToast("Plan deleted.", "success");
    }
  };

  // Native Payment Gateways States
  const [nativePaypalEnabled, setNativePaypalEnabled] = useState(false);
  const [nativePaypalMode, setNativePaypalMode] = useState<"sandbox" | "live">("sandbox");
  const [nativePaypalClientId, setNativePaypalClientId] = useState("");
  const [nativePaypalSecret, setNativePaypalSecret] = useState("");

  const [sslCommerzEnabled, setSslCommerzEnabled] = useState(false);
  const [sslCommerzMode, setSslCommerzMode] = useState<"sandbox" | "live">("sandbox");
  const [sslCommerzStoreId, setSslCommerzStoreId] = useState("");
  const [sslCommerzStorePassword, setSslCommerzStorePassword] = useState("");

  const [bkashEnabled, setBkashEnabled] = useState(false);
  const [bkashMode, setBkashMode] = useState<"sandbox" | "live">("sandbox");
  const [bkashAppKey, setBkashAppKey] = useState("");
  const [bkashAppSecret, setBkashAppSecret] = useState("");
  const [bkashUsername, setBkashUsername] = useState("");
  const [bkashPassword, setBkashPassword] = useState("");

  const [nagadEnabled, setNagadEnabled] = useState(false);
  const [nagadMode, setNagadMode] = useState<"sandbox" | "live">("sandbox");
  const [nagadMerchantId, setNagadMerchantId] = useState("");
  const [nagadPrivateKey, setNagadPrivateKey] = useState("");
  const [nagadPublicKey, setNagadPublicKey] = useState("");

  const [rocketEnabled, setRocketEnabled] = useState(false);
  const [rocketMode, setRocketMode] = useState<"sandbox" | "live">("sandbox");
  const [rocketMerchantId, setRocketMerchantId] = useState("");
  const [rocketApiKey, setRocketApiKey] = useState("");

  interface ManualPayment {
    id: string;
    title: string;
    iconUrl: string;
    instructions: string;
    enabled: boolean;
  }
  const [manualPayments, setManualPayments] = useState<ManualPayment[]>([]);

  // Taxes States
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxRate, setTaxRate] = useState("15");
  const [taxCalculation, setTaxCalculation] = useState<"inclusive" | "exclusive">("exclusive");

  // Checkout States
  const [checkoutRedirect, setCheckoutRedirect] = useState(true);
  const [guestCheckout, setGuestCheckout] = useState(false);
  const [enableTerms, setEnableTerms] = useState(false);
  const [termsPageUrl, setTermsPageUrl] = useState("/terms");

  // Referrals States
  const [enableReferrals, setEnableReferrals] = useState(true);
  const [referralCodeType, setReferralCodeType] = useState<"username" | "random">("username");
  const [referralEligibility, setReferralEligibility] = useState<"global" | "instructors" | "specific">("global");
  const [specificReferralUsers, setSpecificReferralUsers] = useState("");
  const [referralCommissionRate, setReferralCommissionRate] = useState("10");
  const [referredDiscountRate, setReferredDiscountRate] = useState("10");

  // Design Settings States
  const [primaryColor, setPrimaryColor] = useState("#ff0000");
  const [secondaryColor, setSecondaryColor] = useState("#4f46e5");
  const [textColor, setTextColor] = useState("#1f2937");
  const [typographyFamily, setTypographyFamily] = useState("inter");
  const [lmsAdminLogoUrl, setLmsAdminLogoUrl] = useState("");
  const [lmsAdminLogoId, setLmsAdminLogoId] = useState("");
  const [userDashboardLogoUrl, setUserDashboardLogoUrl] = useState("");
  const [userDashboardLogoId, setUserDashboardLogoId] = useState("");

  // Page Features Settings States
  const [pageFeatureInstructorInfo, setPageFeatureInstructorInfo] = useState(false);
  const [pageFeatureWishlist, setPageFeatureWishlist] = useState(true);
  const [pageFeatureQA, setPageFeatureQA] = useState(false);
  const [pageFeatureAuthor, setPageFeatureAuthor] = useState(false);
  const [pageFeatureLevel, setPageFeatureLevel] = useState(true);
  const [pageFeatureSocialShare, setPageFeatureSocialShare] = useState(true);
  const [pageFeatureDuration, setPageFeatureDuration] = useState(true);
  const [pageFeatureTotalEnrolled, setPageFeatureTotalEnrolled] = useState(true);
  const [pageFeatureUpdateDate, setPageFeatureUpdateDate] = useState(true);
  const [pageFeatureProgressBar, setPageFeatureProgressBar] = useState(true);
  const [pageFeatureMaterial, setPageFeatureMaterial] = useState(true);
  const [pageFeatureAbout, setPageFeatureAbout] = useState(true);
  const [pageFeatureDescription, setPageFeatureDescription] = useState(true);
  const [pageFeatureBenefits, setPageFeatureBenefits] = useState(true);
  const [pageFeatureRequirements, setPageFeatureRequirements] = useState(true);
  const [pageFeatureTargetAudience, setPageFeatureTargetAudience] = useState(true);
  const [pageFeatureAnnouncements, setPageFeatureAnnouncements] = useState(true);
  const [pageFeatureReview, setPageFeatureReview] = useState(true);
  const [pageFeatureStickySidebar, setPageFeatureStickySidebar] = useState(false);

  // Zoom Settings States
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [zoomAuthType, setZoomAuthType] = useState<"jwt" | "oauth">("jwt");
  const [zoomClientId, setZoomClientId] = useState("");
  const [zoomClientSecret, setZoomClientSecret] = useState("");
  const [zoomApiSecret, setZoomApiSecret] = useState("");
  const [zoomAccountEmail, setZoomAccountEmail] = useState("");
  const [zoomDefaultDuration, setZoomDefaultDuration] = useState("60");
  const [zoomDefaultTimezone, setZoomDefaultTimezone] = useState("Asia/Dhaka");
  const [zoomAutoRecord, setZoomAutoRecord] = useState(true);
  const [zoomWaitingRoom, setZoomWaitingRoom] = useState(true);
  const [zoomRecordingStorage, setZoomRecordingStorage] = useState("cloud");
  const [zoomConnected, setZoomConnected] = useState(false);

  // Authentication Settings States
  const [enable2fa, setEnable2fa] = useState(false);
  const [enableFraudProtection, setEnableFraudProtection] = useState(false);
  const [limitActiveLoginSessions, setLimitActiveLoginSessions] = useState(false);
  const [enableEmailVerification, setEnableEmailVerification] = useState(false);
  const [enableGoogleLogin, setEnableGoogleLogin] = useState(false);
  const [enableFacebookLogin, setEnableFacebookLogin] = useState(false);
  const [enableTwitterLogin, setEnableTwitterLogin] = useState(false);

  // GreenWeb SMS & Academic class settings states
  const [greenwebApiToken, setGreenwebApiToken] = useState("");
  const [otpMessageTemplate, setOtpMessageTemplate] = useState("");
  const [dynamicClasses, setDynamicClasses] = useState("");
  const [sscBatches, setSscBatches] = useState("");
  const [hscBatches, setHscBatches] = useState("");

  // UI Status States
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const { showToast } = useToast();

  const tabs = [
    { key: "general", label: "General", icon: Settings },
    { key: "course", label: "Course", icon: BookOpen },
    { key: "evaluation", label: "Evaluation", icon: ClipboardCheck },
    { key: "monetization", label: "Monetization", icon: DollarSign },
    { key: "design", label: "Design", icon: Palette },
    { key: "zoom", label: "Zoom", icon: Video },
    { key: "authentication", label: "Authentication", icon: Shield },
    { key: "avatars", label: "Avatars Preset", icon: User },
  ];

  // Fetch Settings on Mount
  useEffect(() => {
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce;

    if (!ajaxUrl || !(window as any).jQuery) {
      setIsLoading(false);
      return;
    }

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_lms_settings",
        nonce: adminNonce,
      },
      success: (res: any) => {
        if (res.success && res.data) {
          const s = res.data;
          
          const getSettingVal = (key: string, defaultValue = ""): string => {
            const val = s[key] !== undefined ? s[key] : s[key.toLowerCase()];
            return val !== undefined ? String(val) : defaultValue;
          };

          const getSettingBool = (key: string, defaultValue = false): boolean => {
            const val = s[key] !== undefined ? s[key] : s[key.toLowerCase()];
            if (val === undefined) return defaultValue;
            return val === "true" || val === true || val === 1 || val === "1";
          };

          // General
          setEnableMarketplace(getSettingBool("enableMarketplace", true));
          setPublicProfile(getSettingBool("publicProfile", true));
          setCoursesPerPage(getSettingVal("coursesPerPage", "20"));
          setCustomDashboardUrl(getSettingVal("customDashboardUrl", "/dashboard"));
          
          setBecomeInstructorButton(getSettingBool("becomeInstructorButton", true));
          setAllowPublishCourses(getSettingBool("allowPublishCourses", true));
          setAllowTrashCourses(getSettingBool("allowTrashCourses", true));
          setAllowChangeAuthor(getSettingBool("allowChangeAuthor", false));
          setAllowResetProgress(getSettingBool("allowResetProgress", true));

          // Course
          setEnableCodeEditor(getSettingBool("enableCodeEditor", true));
          setVideoAutoPlay(getSettingBool("videoAutoPlay", false));
          setCourseRetake(getSettingBool("courseRetake", true));
          setLessonSidebarToggling(getSettingBool("lessonSidebarToggling", true));
          setCourseCompletionMode(getSettingVal("courseCompletionMode", "strict") as "strict" | "flexible");
          setMaxVideoUploadSize(getSettingVal("maxVideoUploadSize", "512"));

          // Preferred Video Sources
          setVideoSourceHtml5(getSettingBool("videoSourceHtml5", true));
          setVideoSourceExternal(getSettingBool("videoSourceExternal", true));
          setVideoSourceYoutube(getSettingBool("videoSourceYoutube", true));
          setVideoSourceVimeo(getSettingBool("videoSourceVimeo", true));
          setVideoSourceEmbedded(getSettingBool("videoSourceEmbedded", true));
          setVideoSourceShortcode(getSettingBool("videoSourceShortcode", true));

          // Tutor Player Toggles
          setTutorPlayerYoutube(getSettingBool("tutorPlayerYoutube", false));
          setTutorPlayerVimeo(getSettingBool("tutorPlayerVimeo", false));

          // Evaluation
          setEnableQuizTimer(getSettingBool("enableQuizTimer", true));
          setShowCorrectAnswers(getSettingBool("showCorrectAnswers", false));
          setEnableAssignmentUpload(getSettingBool("enableAssignmentUpload", true));
          setMaxAssignmentFileSize(getSettingVal("maxAssignmentFileSize", "128"));
          setPassingGradeDefault(getSettingVal("passingGradeDefault", "70"));

          // Monetization
          setPaymentEngine(getSettingVal("paymentEngine", "woocommerce"));
          setEarningEnabled(getSettingBool("earningEnabled", true));
          setAdminCommission(getSettingVal("adminCommission", "20"));
          setPayoutPaypal(getSettingBool("payoutPaypal", true));
          setPayoutBank(getSettingBool("payoutBank", false));
          setPayoutStripe(getSettingBool("payoutStripe", true));

          // Native Payment Gateways
          setNativePaypalEnabled(getSettingBool("nativePaypalEnabled", false));
          setNativePaypalMode(getSettingVal("nativePaypalMode", "sandbox") as "sandbox" | "live");
          setNativePaypalClientId(getSettingVal("nativePaypalClientId", ""));
          setNativePaypalSecret(getSettingVal("nativePaypalSecret", ""));

          setSslCommerzEnabled(getSettingBool("sslCommerzEnabled", false));
          setSslCommerzMode(getSettingVal("sslCommerzMode", "sandbox") as "sandbox" | "live");
          setSslCommerzStoreId(getSettingVal("sslCommerzStoreId", ""));
          setSslCommerzStorePassword(getSettingVal("sslCommerzStorePassword", ""));

          setBkashEnabled(getSettingBool("bkashEnabled", false));
          setBkashMode(getSettingVal("bkashMode", "sandbox") as "sandbox" | "live");
          setBkashAppKey(getSettingVal("bkashAppKey", ""));
          setBkashAppSecret(getSettingVal("bkashAppSecret", ""));
          setBkashUsername(getSettingVal("bkashUsername", ""));
          setBkashPassword(getSettingVal("bkashPassword", ""));

          setNagadEnabled(getSettingBool("nagadEnabled", false));
          setNagadMode(getSettingVal("nagadMode", "sandbox") as "sandbox" | "live");
          setNagadMerchantId(getSettingVal("nagadMerchantId", ""));
          setNagadPrivateKey(getSettingVal("nagadPrivateKey", ""));
          setNagadPublicKey(getSettingVal("nagadPublicKey", ""));

          setRocketEnabled(getSettingBool("rocketEnabled", false));
          setRocketMode(getSettingVal("rocketMode", "sandbox") as "sandbox" | "live");
          setRocketMerchantId(getSettingVal("rocketMerchantId", ""));
          setRocketApiKey(getSettingVal("rocketApiKey", ""));

          // Manual Payments
          const manualRaw = getSettingVal("manual_payments", "[]");
          try {
            const parsed = JSON.parse(manualRaw);
            if (Array.isArray(parsed)) {
              setManualPayments(parsed);
            }
          } catch (e) {
            console.error("Failed to parse manual payments", e);
          }

          // Avatar Presets
          const avatarRaw = getSettingVal("avatar_gallery", "[]");
          try {
            const parsed = JSON.parse(avatarRaw);
            if (Array.isArray(parsed)) {
              setAvatarGallery(parsed);
            }
          } catch (e) {
            console.error("Failed to parse avatar gallery", e);
          }

          // Subscriptions
          const subRaw = getSettingVal("subscriptionPlans", "[]");
          try {
            const parsed = JSON.parse(subRaw);
            if (Array.isArray(parsed)) {
              setSubscriptionPlans(parsed);
            }
          } catch (e) {
            console.error("Failed to parse subscription plans", e);
            setSubscriptionPlans([]);
          }

          // Taxes
          setTaxEnabled(getSettingBool("taxEnabled", false));
          setTaxRate(getSettingVal("taxRate", "15"));
          setTaxCalculation(getSettingVal("taxCalculation", "exclusive") as "inclusive" | "exclusive");

          // Checkout
          setCheckoutRedirect(getSettingBool("checkoutRedirect", true));
          setGuestCheckout(getSettingBool("guestCheckout", false));
          setEnableTerms(getSettingBool("enableTerms", false));
          setTermsPageUrl(getSettingVal("termsPageUrl", "/terms"));

          // Referrals
          setEnableReferrals(getSettingBool("enableReferrals", true));
          setReferralCodeType(getSettingVal("referralCodeType", "username") as any);
          setReferralEligibility(getSettingVal("referralEligibility", "global") as any);
          setSpecificReferralUsers(getSettingVal("specificReferralUsers", ""));
          setReferralCommissionRate(getSettingVal("referralCommissionRate", "10"));
          setReferredDiscountRate(getSettingVal("referredDiscountRate", "10"));

          // Design
          setPrimaryColor(getSettingVal("primaryColor", "#ff0000"));
          setSecondaryColor(getSettingVal("secondaryColor", "#4f46e5"));
          setTextColor(getSettingVal("textColor", "#1f2937"));
          setTypographyFamily(getSettingVal("typographyFamily", "inter"));
          setLmsAdminLogoUrl(getSettingVal("lmsAdminLogoUrl", ""));
          setLmsAdminLogoId(getSettingVal("lmsAdminLogoId", ""));
          setUserDashboardLogoUrl(getSettingVal("userDashboardLogoUrl", ""));
          setUserDashboardLogoId(getSettingVal("userDashboardLogoId", ""));

          // Page Features
          setPageFeatureInstructorInfo(getSettingBool("pageFeatureInstructorInfo", false));
          setPageFeatureWishlist(getSettingBool("pageFeatureWishlist", true));
          setPageFeatureQA(getSettingBool("pageFeatureQA", false));
          setPageFeatureAuthor(getSettingBool("pageFeatureAuthor", false));
          setPageFeatureLevel(getSettingBool("pageFeatureLevel", true));
          setPageFeatureSocialShare(getSettingBool("pageFeatureSocialShare", true));
          setPageFeatureDuration(getSettingBool("pageFeatureDuration", true));
          setPageFeatureTotalEnrolled(getSettingBool("pageFeatureTotalEnrolled", true));
          setPageFeatureUpdateDate(getSettingBool("pageFeatureUpdateDate", true));
          setPageFeatureProgressBar(getSettingBool("pageFeatureProgressBar", true));
          setPageFeatureMaterial(getSettingBool("pageFeatureMaterial", true));
          setPageFeatureAbout(getSettingBool("pageFeatureAbout", true));
          setPageFeatureDescription(getSettingBool("pageFeatureDescription", true));
          setPageFeatureBenefits(getSettingBool("pageFeatureBenefits", true));
          setPageFeatureRequirements(getSettingBool("pageFeatureRequirements", true));
          setPageFeatureTargetAudience(getSettingBool("pageFeatureTargetAudience", true));
          setPageFeatureAnnouncements(getSettingBool("pageFeatureAnnouncements", true));
          setPageFeatureReview(getSettingBool("pageFeatureReview", true));
          setPageFeatureStickySidebar(getSettingBool("pageFeatureStickySidebar", false));

          // Zoom
          setZoomEnabled(getSettingBool("zoomEnabled", false));
          setZoomAuthType(getSettingVal("zoomAuthType", "jwt") as "jwt" | "oauth");
          setZoomClientId(getSettingVal("zoomClientId", ""));
          setZoomClientSecret(getSettingVal("zoomClientSecret", ""));
          setZoomApiSecret(getSettingVal("zoomApiSecret", ""));
          setZoomAccountEmail(getSettingVal("zoomAccountEmail", ""));
          setZoomDefaultDuration(getSettingVal("zoomDefaultDuration", "60"));
          setZoomDefaultTimezone(getSettingVal("zoomDefaultTimezone", "Asia/Dhaka"));
          setZoomAutoRecord(getSettingBool("zoomAutoRecord", true));
          setZoomWaitingRoom(getSettingBool("zoomWaitingRoom", true));
          setZoomRecordingStorage(getSettingVal("zoomRecordingStorage", "cloud"));
          setZoomConnected(getSettingBool("zoomConnected", false));

          // Authentication
          setEnable2fa(getSettingBool("enable_2fa", false));
          setEnableFraudProtection(getSettingBool("enable_fraud_protection", false));
          setLimitActiveLoginSessions(getSettingBool("limit_active_login_sessions", false));
          setEnableEmailVerification(getSettingBool("enable_email_verification", false));
          setEnableGoogleLogin(getSettingBool("enable_google_login", false));
          setEnableFacebookLogin(getSettingBool("enable_facebook_login", false));
          setEnableTwitterLogin(getSettingBool("enable_twitter_login", false));

          setGreenwebApiToken(getSettingVal("greenweb_api_token", ""));
          setOtpMessageTemplate(getSettingVal("otp_message_template", ""));
          setDynamicClasses(getSettingVal("dynamic_classes", ""));
          setSscBatches(getSettingVal("ssc_batches", ""));
          setHscBatches(getSettingVal("hsc_batches", ""));
        }
        setIsLoading(false);
      },
      error: () => {
        setIsLoading(false);
        showToast("Failed to fetch settings from database.", "error");
      }
    });
  }, []);

  // Save Settings Action
  const handleSaveSettings = () => {
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce;

    if (!ajaxUrl || !(window as any).jQuery) {
      showToast("Cannot save settings. WordPress API not available.", "error");
      return;
    }

    setIsSaving(true);

    const payload = {
      enableMarketplace,
      publicProfile,
      coursesPerPage,
      customDashboardUrl,
      
      becomeInstructorButton,
      allowPublishCourses,
      allowTrashCourses,
      allowChangeAuthor,
      allowResetProgress,

      enableCodeEditor,
      videoAutoPlay,
      courseRetake,
      lessonSidebarToggling,
      courseCompletionMode,
      maxVideoUploadSize,

      videoSourceHtml5,
      videoSourceExternal,
      videoSourceYoutube,
      videoSourceVimeo,
      videoSourceEmbedded,
      videoSourceShortcode,
      tutorPlayerYoutube,
      tutorPlayerVimeo,

      enableQuizTimer,
      showCorrectAnswers,
      enableAssignmentUpload,
      maxAssignmentFileSize,
      passingGradeDefault,

      paymentEngine,
      earningEnabled,
      adminCommission,
      payoutPaypal,
      payoutBank,
      payoutStripe,

      nativePaypalEnabled,
      nativePaypalMode,
      nativePaypalClientId,
      nativePaypalSecret,

      sslCommerzEnabled,
      sslCommerzMode,
      sslCommerzStoreId,
      sslCommerzStorePassword,

      bkashEnabled,
      bkashMode,
      bkashAppKey,
      bkashAppSecret,
      bkashUsername,
      bkashPassword,

      nagadEnabled,
      nagadMode,
      nagadMerchantId,
      nagadPrivateKey,
      nagadPublicKey,

      rocketEnabled,
      rocketMode,
      rocketMerchantId,
      rocketApiKey,

      manual_payments: JSON.stringify(manualPayments),
      subscriptionPlans: JSON.stringify(subscriptionPlans),

      taxEnabled,
      taxRate,
      taxCalculation,

      checkoutRedirect,
      guestCheckout,
      enableTerms,
      termsPageUrl,

      enableReferrals,
      referralCodeType,
      referralEligibility,
      specificReferralUsers,
      referralCommissionRate,
      referredDiscountRate,

      primaryColor,
      secondaryColor,
      textColor,
      typographyFamily,
      lmsAdminLogoUrl,
      lmsAdminLogoId,
      userDashboardLogoUrl,
      userDashboardLogoId,

      pageFeatureInstructorInfo,
      pageFeatureWishlist,
      pageFeatureQA,
      pageFeatureAuthor,
      pageFeatureLevel,
      pageFeatureSocialShare,
      pageFeatureDuration,
      pageFeatureTotalEnrolled,
      pageFeatureUpdateDate,
      pageFeatureProgressBar,
      pageFeatureMaterial,
      pageFeatureAbout,
      pageFeatureDescription,
      pageFeatureBenefits,
      pageFeatureRequirements,
      pageFeatureTargetAudience,
      pageFeatureAnnouncements,
      pageFeatureReview,
      pageFeatureStickySidebar,

      zoomEnabled,
      zoomAuthType,
      zoomClientId,
      zoomClientSecret,
      zoomApiSecret,
      zoomAccountEmail,
      zoomDefaultDuration,
      zoomDefaultTimezone,
      zoomAutoRecord,
      zoomWaitingRoom,
      zoomRecordingStorage,
      zoomConnected,
      enable_2fa: enable2fa,
      enable_fraud_protection: enableFraudProtection,
      limit_active_login_sessions: limitActiveLoginSessions,
      enable_email_verification: enableEmailVerification,
      enable_google_login: enableGoogleLogin,
      enable_facebook_login: enableFacebookLogin,
      enable_twitter_login: enableTwitterLogin,
      greenweb_api_token: greenwebApiToken,
      otp_message_template: otpMessageTemplate,
      dynamic_classes: dynamicClasses,
      ssc_batches: sscBatches,
      hsc_batches: hscBatches,
      avatar_gallery: JSON.stringify(avatarGallery),
    };

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_save_lms_settings",
        nonce: adminNonce,
        settings: payload,
      },
      success: (res: any) => {
        setIsSaving(false);
        if (res.success) {
          showToast(res.data?.message || "Settings saved successfully!", "success");
        } else {
          showToast(res.data || "Failed to save settings.", "error");
        }
      },
      error: () => {
        setIsSaving(false);
        showToast("An error occurred while saving settings.", "error");
      }
    });
  };

  // WordPress Media Library for Logo Uploads
  const uploadLogo = (type: "lmsAdmin" | "userDashboard") => {
    const wp = (window as any).wp;
    if (wp && wp.media) {
      const frame = wp.media({
        title: `Select or Upload ${type === "lmsAdmin" ? "LMS Admin" : "User Dashboard"} Logo`,
        button: {
          text: "Use this logo",
        },
        multiple: false,
      });

      frame.on("select", () => {
        const attachment = frame.state().get("selection").first().toJSON();
        if (type === "lmsAdmin") {
          setLmsAdminLogoUrl(attachment.url);
          setLmsAdminLogoId(attachment.id.toString());
        } else {
          setUserDashboardLogoUrl(attachment.url);
          setUserDashboardLogoId(attachment.id.toString());
        }
      });

      frame.open();
    } else {
      alert("WordPress Media Library is not loaded. Please ensure wp_enqueue_media() was executed in PHP.");
    }
  };

  const renderLoadingSkeleton = () => (
    <div className="animate-pulse space-y-6">
      <div className="h-6 bg-gray-200 rounded w-1/4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="space-y-4 mt-8">
        <div className="h-10 bg-gray-100 rounded"></div>
        <div className="h-10 bg-gray-100 rounded"></div>
        <div className="h-10 bg-gray-100 rounded"></div>
      </div>
    </div>
  );

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-800">
            Settings
          </h1>
          <p className="text-sm text-[#5A6A85] mt-1">
            Configure your LMS platform preferences
          </p>
        </div>
        <button
          onClick={handleSaveSettings}
          disabled={isLoading || isSaving}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#FF2D55] hover:bg-[#E11D48] text-white font-bold text-sm rounded-full transition-all active:scale-[0.98] shadow-sm cursor-pointer disabled:opacity-50 border-none flex-shrink-0"
        >
          {isSaving ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>Save Settings</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Sidebar Tabs */}
        <div className="lg:col-span-3">
          <div className="bg-white border border-slate-200 rounded-[12px] p-3 shadow-sm">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.key;
                return (
                  <div key={tab.key} className="space-y-1">
                    <button
                      onClick={() => {
                        setActiveTab(tab.key as typeof activeTab);
                        if (tab.key === "monetization") {
                          setActiveMonetizationSubTab("payment-methods");
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors text-left cursor-pointer border-none ${
                        isActive
                          ? "bg-[#FF2D55]/8 text-[#FF2D55] font-semibold"
                          : "text-[#5A6A85] hover:bg-slate-50 hover:text-slate-900 font-medium"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </button>

                    {/* Submenu for Monetization */}
                    {tab.key === "monetization" && isActive && (
                      <div className="pl-9 pr-2 py-1 space-y-1 border-l border-slate-200 ml-6">
                        {[
                          { key: "payment-methods", label: "Payment Methods" },
                          { key: "subscriptions", label: "Subscriptions" },
                          { key: "taxes", label: "Taxes" },
                          { key: "checkout", label: "Checkout" },
                          { key: "referrals", label: "Referrals" },
                        ].map((sub) => (
                          <button
                            key={sub.key}
                            onClick={() => setActiveMonetizationSubTab(sub.key as any)}
                            className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors cursor-pointer border-none ${
                              activeMonetizationSubTab === sub.key
                                ? "text-[#FF2D55] bg-[#FF2D55]/5 font-semibold"
                                : "text-[#5A6A85] hover:text-slate-900 hover:bg-slate-50"
                            }`}
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Right Content Pane */}
        <div className="lg:col-span-9">
          {isLoading ? (
            <Card title="Loading Settings..." description="Fetching options from database">
              {renderLoadingSkeleton()}
            </Card>
          ) : (
            <>
              {/* ─── GENERAL ─── */}
              {activeTab === "general" && (
                <Card
                  title="General Settings"
                  description="Platform configuration and global rules"
                >
                  <div className="space-y-6">
                    <Toggle
                      label="Enable Marketplace"
                      description="Allow multiple instructors to sell courses on the platform"
                      checked={enableMarketplace}
                      onChange={setEnableMarketplace}
                    />
                    <hr className="border-gray-100" />
                    <Toggle
                      label="Public Profile"
                      description="Make instructor profiles visible to the public"
                      checked={publicProfile}
                      onChange={setPublicProfile}
                    />
                    <hr className="border-gray-100" />
                    <Select
                      label="Courses Per Page"
                      options={[
                        { value: "10", label: "10" },
                        { value: "20", label: "20" },
                        { value: "50", label: "50" },
                        { value: "100", label: "100" },
                      ]}
                      value={coursesPerPage}
                      onChange={setCoursesPerPage}
                    />
                    <Input
                      label="Custom Dashboard URL"
                      placeholder="/dashboard"
                      value={customDashboardUrl}
                      onChange={(e) => setCustomDashboardUrl(e.target.value)}
                    />
                    <hr className="border-gray-100" />
                    <div className="space-y-4 pt-2">
                      <h4 className="text-sm font-bold text-gray-800">Become an Instructor & Permissions</h4>
                      <Toggle
                        label="Join as Instructor Button"
                        description="Enable the option to display the 'Join as Instructor' banner above the footer section."
                        checked={becomeInstructorButton}
                        onChange={setBecomeInstructorButton}
                      />
                      <Toggle
                        label="Allow Instructors to Publish Courses"
                        description="Enable instructors to publish the course directly. If disabled, admins will be able to review course content before publishing."
                        checked={allowPublishCourses}
                        onChange={setAllowPublishCourses}
                      />
                      <Toggle
                        label="Allow Instructors to Trash Courses"
                        description="Enable this setting to allow instructors to delete courses."
                        checked={allowTrashCourses}
                        onChange={setAllowTrashCourses}
                      />
                      <Toggle
                        label="Allow Instructors to Change Course Author"
                        description="If enabled, instructors can change the course author for their courses."
                        checked={allowChangeAuthor}
                        onChange={setAllowChangeAuthor}
                      />
                      <Toggle
                        label="Allow Instructors to Reset Student Progress"
                        description="Enable to allow instructors to reset a student’s course progress."
                        checked={allowResetProgress}
                        onChange={setAllowResetProgress}
                      />
                    </div>
                  </div>
                </Card>
              )}

              {/* ─── COURSE ─── */}
              {activeTab === "course" && (
                <Card
                  title="Course Settings"
                  description="Configure lessons and course structure behavior"
                >
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Toggle
                        label="Enable Code Editor"
                        description="Allow code snippets in lessons"
                        checked={enableCodeEditor}
                        onChange={setEnableCodeEditor}
                      />
                      <Toggle
                        label="Video Auto Play"
                        description="Automatically play next lesson video"
                        checked={videoAutoPlay}
                        onChange={setVideoAutoPlay}
                      />
                      <Toggle
                        label="Course Retake"
                        description="Allow students to retake completed courses"
                        checked={courseRetake}
                        onChange={setCourseRetake}
                      />
                      <Toggle
                        label="Lesson Sidebar Toggling"
                        description="Let students collapse the lesson sidebar"
                        checked={lessonSidebarToggling}
                        onChange={setLessonSidebarToggling}
                      />
                    </div>
                    <hr className="border-gray-100" />
                    <div>
                      <div className="block text-sm font-medium text-[#374151] mb-3">
                        Course Completing Process
                      </div>
                      <div className="space-y-3">
                        <div
                          onClick={() => setCourseCompletionMode("strict")}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            courseCompletionMode === "strict"
                              ? "bg-red-50/30 border-[#ff0000]/30"
                              : "border-gray-200 bg-gray-50/50 hover:border-[#ff0000]/20"
                          }`}
                        >
                          <input
                            type="radio"
                            name="completion"
                            checked={courseCompletionMode === "strict"}
                            onChange={() => setCourseCompletionMode("strict")}
                            className="mt-0.5 text-[#ff0000]"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              Strict
                            </p>
                            <p className="text-xs text-gray-500">
                              Students must complete all lessons in order
                            </p>
                          </div>
                        </div>
                        <div
                          onClick={() => setCourseCompletionMode("flexible")}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            courseCompletionMode === "flexible"
                              ? "bg-red-50/30 border-[#ff0000]/30"
                              : "border-gray-200 bg-gray-50/50 hover:border-[#ff0000]/20"
                          }`}
                        >
                          <input
                            type="radio"
                            name="completion"
                            checked={courseCompletionMode === "flexible"}
                            onChange={() => setCourseCompletionMode("flexible")}
                            className="mt-0.5 text-[#ff0000]"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              Flexible
                            </p>
                            <p className="text-xs text-gray-500">
                              Students can jump between lessons freely
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Input
                      label="Maximum Video Upload Size (MB)"
                      type="number"
                      value={maxVideoUploadSize}
                      onChange={(e) => setMaxVideoUploadSize(e.target.value)}
                    />
                  </div>
                </Card>
              )}

              {activeTab === "course" && (
                <Card
                  title="Video"
                  description="Configure preferred video sources and custom player options"
                >
                  <div className="space-y-6">
                    <div>
                      <span className="block text-sm font-bold text-slate-800 mb-1">Preferred Video Source</span>
                      <span className="block text-xs text-slate-500 mb-4">Select the video hosting platform(s) you want to enable.</span>
                      
                      <div className="space-y-3 pl-1">
                        {[
                          { key: "html5", label: "HTML 5 (mp4)", state: videoSourceHtml5, setter: setVideoSourceHtml5 },
                          { key: "external", label: "External URL", state: videoSourceExternal, setter: setVideoSourceExternal },
                          { key: "youtube", label: "YouTube", state: videoSourceYoutube, setter: setVideoSourceYoutube },
                          { key: "vimeo", label: "Vimeo", state: videoSourceVimeo, setter: setVideoSourceVimeo },
                          { key: "embedded", label: "Embedded", state: videoSourceEmbedded, setter: setVideoSourceEmbedded },
                          { key: "shortcode", label: "Shortcode", state: videoSourceShortcode, setter: setVideoSourceShortcode },
                        ].map((item) => (
                          <label key={item.key} className="flex items-center gap-3.5 cursor-pointer py-0.5 group select-none">
                            <div className="relative flex items-center">
                              <input
                                type="checkbox"
                                checked={item.state}
                                onChange={(e) => item.setter(e.target.checked)}
                                className="sr-only"
                              />
                              <div className={`w-5 h-5 rounded-[6px] border flex items-center justify-center transition-all ${
                                item.state 
                                  ? "bg-[#2563EB] border-[#2563EB] text-white shadow-sm" 
                                  : "border-gray-300 bg-white group-hover:border-gray-400"
                                }`}>
                                {item.state && (
                                  <svg className="w-3.5 h-3.5 stroke-[3] stroke-current" fill="none" viewBox="0 0 24 24">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                )}
                              </div>
                            </div>
                            <span className="text-sm font-bold text-slate-800 tracking-tight leading-none group-hover:text-slate-900 transition-colors">
                              {item.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <hr className="border-gray-100 my-5" />

                    <div className="space-y-5">
                      <Toggle
                        label="Use Tutor Player for YouTube"
                        description="Enable this option to use Tutor LMS video player for YouTube."
                        checked={tutorPlayerYoutube}
                        onChange={setTutorPlayerYoutube}
                      />
                      <hr className="border-gray-50" />
                      <Toggle
                        label="Use Tutor Player for Vimeo"
                        description="Enable this option to use Tutor LMS video player for Vimeo."
                        checked={tutorPlayerVimeo}
                        onChange={setTutorPlayerVimeo}
                      />
                    </div>
                  </div>
                </Card>
              )}

              {/* ─── EVALUATION ─── */}
              {activeTab === "evaluation" && (
                <Card
                  title="Evaluation Settings"
                  description="Quiz and assignment configuration"
                >
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Toggle
                        label="Enable Quiz Timer Display"
                        description="Show remaining time to students during quizzes"
                        checked={enableQuizTimer}
                        onChange={setEnableQuizTimer}
                      />
                      <Toggle
                        label="Show Correct Answers on Fail"
                        description="Reveal correct answers when students fail a quiz"
                        checked={showCorrectAnswers}
                        onChange={setShowCorrectAnswers}
                      />
                      <Toggle
                        label="Enable File Upload for Assignments"
                        description="Allow students to upload files with assignments"
                        checked={enableAssignmentUpload}
                        onChange={setEnableAssignmentUpload}
                      />
                    </div>
                    <hr className="border-gray-100" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        label="Maximum Assignment File Size (MB)"
                        type="number"
                        value={maxAssignmentFileSize}
                        onChange={(e) => setMaxAssignmentFileSize(e.target.value)}
                      />
                      <Input
                        label="Passing Grade Percentage Default"
                        type="number"
                        value={passingGradeDefault}
                        onChange={(e) => setPassingGradeDefault(e.target.value)}
                      />
                    </div>
                  </div>
                </Card>
              )}

              {/* ─── MONETIZATION ─── */}
              {activeTab === "monetization" && (
                <div className="space-y-6">
                  {activeMonetizationSubTab === "payment-methods" && (
                    <Card
                      title="Payment Methods"
                      description="Choose payment engine and configure active gateways"
                    >
                      <div className="space-y-6">
                        <Select
                          label="Payment Engine"
                          options={[
                            { value: "woocommerce", label: "WooCommerce" },
                            { value: "native", label: "Native Payment Gateways" },
                            { value: "disable", label: "Disable Monetization" },
                          ]}
                          value={paymentEngine}
                          onChange={setPaymentEngine}
                        />

                        {paymentEngine === "woocommerce" && (
                          <div className="space-y-6 border-t border-gray-100 pt-6">
                            <Toggle
                              label="Enable Earning"
                              description="Track and distribute instructor earnings"
                              checked={earningEnabled}
                              onChange={setEarningEnabled}
                            />
                            {earningEnabled && (
                              <>
                                <Input
                                  label="Admin Commission Percentage (%)"
                                  type="number"
                                  value={adminCommission}
                                  onChange={(e) => setAdminCommission(e.target.value)}
                                />
                                <div>
                                  <div className="block text-sm font-medium text-[#374151] mb-3">
                                    Supported Payout Gateways
                                  </div>
                                  <div className="space-y-3">
                                    <div
                                      onClick={() => setPayoutPaypal(!payoutPaypal)}
                                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                        payoutPaypal
                                          ? "bg-red-50/30 border-[#ff0000]/30"
                                          : "border-gray-200 bg-gray-50/50 hover:border-[#ff0000]/20"
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={payoutPaypal}
                                        onChange={(e) => setPayoutPaypal(e.target.checked)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="rounded text-[#ff0000]"
                                      />
                                      <CreditCard className="h-4 w-4 text-[#5A6A85]" />
                                      <span className="text-sm text-[#374151]">
                                        PayPal
                                      </span>
                                    </div>
                                    <div
                                      onClick={() => setPayoutBank(!payoutBank)}
                                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                        payoutBank
                                          ? "bg-red-50/30 border-[#ff0000]/30"
                                          : "border-gray-200 bg-gray-50/50 hover:border-[#ff0000]/20"
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={payoutBank}
                                        onChange={(e) => setPayoutBank(e.target.checked)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="rounded text-[#ff0000]"
                                      />
                                      <Landmark className="h-4 w-4 text-[#5A6A85]" />
                                      <span className="text-sm text-[#374151]">
                                        Bank Transfer
                                      </span>
                                    </div>
                                    <div
                                      onClick={() => setPayoutStripe(!payoutStripe)}
                                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                        payoutStripe
                                          ? "bg-red-50/30 border-[#ff0000]/30"
                                          : "border-gray-200 bg-gray-50/50 hover:border-[#ff0000]/20"
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={payoutStripe}
                                        onChange={(e) => setPayoutStripe(e.target.checked)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="rounded text-[#ff0000]"
                                      />
                                      <Building2 className="h-4 w-4 text-[#5A6A85]" />
                                      <span className="text-sm text-[#374151]">
                                        Stripe
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {paymentEngine === "native" && (
                          <div className="space-y-6 border-t border-gray-100 pt-6">
                            <div>
                              <h3 className="text-sm font-semibold text-gray-800 mb-4">
                                Native Payment Gateways
                              </h3>
                              <div className="space-y-4">
                                {/* bKash */}
                                <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="h-10 w-10 rounded-lg bg-pink-50 flex items-center justify-center font-bold text-pink-600 text-xs shrink-0">
                                        bKash
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-gray-800">bKash Payment Gateway</p>
                                        <p className="text-xs text-[#5A6A85]">Receive automated bKash payments</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedGateway(expandedGateway === "bkash" ? null : "bkash")}
                                        className={`p-2 rounded-lg transition-colors ${
                                          expandedGateway === "bkash" ? "bg-gray-100 text-gray-700" : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                                        }`}
                                      >
                                        <Settings className="h-4 w-4" />
                                      </button>
                                      <Toggle
                                        checked={bkashEnabled}
                                        onChange={setBkashEnabled}
                                      />
                                    </div>
                                  </div>
                                  {expandedGateway === "bkash" && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-4 animate-fadeIn">
                                      <Select
                                        label="Gateway Mode"
                                        options={[
                                          { value: "sandbox", label: "Sandbox (Testing)" },
                                          { value: "live", label: "Live (Production)" }
                                        ]}
                                        value={bkashMode}
                                        onChange={(val) => setBkashMode(val as any)}
                                      />
                                      <Input
                                        label="App Key"
                                        placeholder="Enter bKash App Key"
                                        value={bkashAppKey}
                                        onChange={(e) => setBkashAppKey(e.target.value)}
                                      />
                                      <Input
                                        label="App Secret"
                                        placeholder="Enter bKash App Secret"
                                        type="password"
                                        value={bkashAppSecret}
                                        onChange={(e) => setBkashAppSecret(e.target.value)}
                                      />
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Input
                                          label="API Username"
                                          placeholder="Enter API Username"
                                          value={bkashUsername}
                                          onChange={(e) => setBkashUsername(e.target.value)}
                                        />
                                        <Input
                                          label="API Password"
                                          placeholder="Enter API Password"
                                          type="password"
                                          value={bkashPassword}
                                          onChange={(e) => setBkashPassword(e.target.value)}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Nagad */}
                                <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center font-bold text-orange-600 text-xs shrink-0">
                                        Nagad
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-gray-800">Nagad Payment Gateway</p>
                                        <p className="text-xs text-[#5A6A85]">Accept secure Nagad wallet payments</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedGateway(expandedGateway === "nagad" ? null : "nagad")}
                                        className={`p-2 rounded-lg transition-colors ${
                                          expandedGateway === "nagad" ? "bg-gray-100 text-gray-700" : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                                        }`}
                                      >
                                        <Settings className="h-4 w-4" />
                                      </button>
                                      <Toggle
                                        checked={nagadEnabled}
                                        onChange={setNagadEnabled}
                                      />
                                    </div>
                                  </div>
                                  {expandedGateway === "nagad" && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-4 animate-fadeIn">
                                      <Select
                                        label="Gateway Mode"
                                        options={[
                                          { value: "sandbox", label: "Sandbox (Testing)" },
                                          { value: "live", label: "Live (Production)" }
                                        ]}
                                        value={nagadMode}
                                        onChange={(val) => setNagadMode(val as any)}
                                      />
                                      <Input
                                        label="Merchant ID"
                                        placeholder="Enter Nagad Merchant ID"
                                        value={nagadMerchantId}
                                        onChange={(e) => setNagadMerchantId(e.target.value)}
                                      />
                                      <Input
                                        label="Merchant Private Key"
                                        placeholder="Paste Nagad Private Key"
                                        type="password"
                                        value={nagadPrivateKey}
                                        onChange={(e) => setNagadPrivateKey(e.target.value)}
                                      />
                                      <Input
                                        label="Nagad Public Key"
                                        placeholder="Paste Nagad Public Key"
                                        type="password"
                                        value={nagadPublicKey}
                                        onChange={(e) => setNagadPublicKey(e.target.value)}
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* SSLCommerz */}
                                <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center font-bold text-blue-600 text-xs shrink-0">
                                        SSL
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-gray-800">SSLCommerz Gateway</p>
                                        <p className="text-xs text-[#5A6A85]">Accept Cards, Mobile Banking, and Net Banking</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedGateway(expandedGateway === "ssl" ? null : "ssl")}
                                        className={`p-2 rounded-lg transition-colors ${
                                          expandedGateway === "ssl" ? "bg-gray-100 text-gray-700" : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                                        }`}
                                      >
                                        <Settings className="h-4 w-4" />
                                      </button>
                                      <Toggle
                                        checked={sslCommerzEnabled}
                                        onChange={setSslCommerzEnabled}
                                      />
                                    </div>
                                  </div>
                                  {expandedGateway === "ssl" && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-4 animate-fadeIn">
                                      <Select
                                        label="Gateway Mode"
                                        options={[
                                          { value: "sandbox", label: "Sandbox (Testing)" },
                                          { value: "live", label: "Live (Production)" }
                                        ]}
                                        value={sslCommerzMode}
                                        onChange={(val) => setSslCommerzMode(val as any)}
                                      />
                                      <Input
                                        label="Store ID"
                                        placeholder="Enter SSLCommerz Store ID"
                                        value={sslCommerzStoreId}
                                        onChange={(e) => setSslCommerzStoreId(e.target.value)}
                                      />
                                      <Input
                                        label="Store Password"
                                        placeholder="Enter SSLCommerz Store Password"
                                        type="password"
                                        value={sslCommerzStorePassword}
                                        onChange={(e) => setSslCommerzStorePassword(e.target.value)}
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* Rocket */}
                                <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center font-bold text-purple-600 text-xs shrink-0">
                                        Rocket
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-gray-800">Rocket Payment Gateway</p>
                                        <p className="text-xs text-[#5A6A85]">Receive Dutch-Bangla Rocket mobile banking payments</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedGateway(expandedGateway === "rocket" ? null : "rocket")}
                                        className={`p-2 rounded-lg transition-colors ${
                                          expandedGateway === "rocket" ? "bg-gray-100 text-gray-700" : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                                        }`}
                                      >
                                        <Settings className="h-4 w-4" />
                                      </button>
                                      <Toggle
                                        checked={rocketEnabled}
                                        onChange={setRocketEnabled}
                                      />
                                    </div>
                                  </div>
                                  {expandedGateway === "rocket" && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-4 animate-fadeIn">
                                      <Select
                                        label="Gateway Mode"
                                        options={[
                                          { value: "sandbox", label: "Sandbox (Testing)" },
                                          { value: "live", label: "Live (Production)" }
                                        ]}
                                        value={rocketMode}
                                        onChange={(val) => setRocketMode(val as any)}
                                      />
                                      <Input
                                        label="Merchant ID"
                                        placeholder="Enter Rocket Merchant ID"
                                        value={rocketMerchantId}
                                        onChange={(e) => setRocketMerchantId(e.target.value)}
                                      />
                                      <Input
                                        label="API Key"
                                        placeholder="Enter Rocket API Key"
                                        type="password"
                                        value={rocketApiKey}
                                        onChange={(e) => setRocketApiKey(e.target.value)}
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* PayPal Native */}
                                <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center font-bold text-indigo-600 text-xs shrink-0">
                                        PayPal
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-gray-800">PayPal Express Checkout</p>
                                        <p className="text-xs text-[#5A6A85]">Accept global payments via PayPal accounts & credit cards</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedGateway(expandedGateway === "paypal" ? null : "paypal")}
                                        className={`p-2 rounded-lg transition-colors ${
                                          expandedGateway === "paypal" ? "bg-gray-100 text-gray-700" : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                                        }`}
                                      >
                                        <Settings className="h-4 w-4" />
                                      </button>
                                      <Toggle
                                        checked={nativePaypalEnabled}
                                        onChange={setNativePaypalEnabled}
                                      />
                                    </div>
                                  </div>
                                  {expandedGateway === "paypal" && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-4 animate-fadeIn">
                                      <Select
                                        label="Gateway Mode"
                                        options={[
                                          { value: "sandbox", label: "Sandbox (Testing)" },
                                          { value: "live", label: "Live (Production)" }
                                        ]}
                                        value={nativePaypalMode}
                                        onChange={(val) => setNativePaypalMode(val as any)}
                                      />
                                      <Input
                                        label="Client ID"
                                        placeholder="Enter PayPal Client ID"
                                        value={nativePaypalClientId}
                                        onChange={(e) => setNativePaypalClientId(e.target.value)}
                                      />
                                      <Input
                                        label="Client Secret"
                                        placeholder="Enter PayPal Client Secret"
                                        type="password"
                                        value={nativePaypalSecret}
                                        onChange={(e) => setNativePaypalSecret(e.target.value)}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Manual Payments Section */}
                            <div className="border-t border-gray-100 pt-6">
                              <div className="flex items-center justify-between mb-4">
                                <div>
                                  <h3 className="text-sm font-semibold text-gray-800">Manual & Offline Payments</h3>
                                  <p className="text-xs text-[#5A6A85] mt-0.5">Configure bank transfers or custom manual instructions</p>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setManualTitle("");
                                    setManualIconUrl("");
                                    setManualInstructions("");
                                    setShowManualModal(true);
                                  }}
                                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                                >
                                  Add Method
                                </Button>
                              </div>

                              {manualPayments.length === 0 ? (
                                <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400">
                                  No manual payment methods configured yet.
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {manualPayments.map((method) => (
                                    <div key={method.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3 bg-gray-50/50">
                                      <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded bg-white border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
                                          {method.iconUrl ? (
                                            <img src={method.iconUrl} alt={method.title} className="h-full w-full object-cover" />
                                          ) : (
                                            <Landmark className="h-4 w-4 text-gray-400" />
                                          )}
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-gray-800">{method.title}</p>
                                          <p className="text-xs text-gray-400 truncate max-w-[250px]">{method.instructions}</p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <Toggle
                                          checked={method.enabled}
                                          onChange={(val) => {
                                            setManualPayments(
                                              manualPayments.map((m) =>
                                                m.id === method.id ? { ...m, enabled: val } : m
                                              )
                                            );
                                          }}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setManualPayments(manualPayments.filter((m) => m.id !== method.id));
                                          }}
                                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded-md transition-colors"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Subscriptions sub-tab */}
                  {activeMonetizationSubTab === "subscriptions" && (
                    <Card
                      title="Subscriptions & Pricing Plans"
                      description="Create, edit, and manage custom subscription tiers and pricing plans for students"
                    >
                      <div className="space-y-6">
                        {/* Plans Table */}
                        <div className="border border-gray-150 rounded-2xl overflow-hidden bg-white">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                <th className="px-5 py-3">Plan Name</th>
                                <th className="px-5 py-3">Tier</th>
                                <th className="px-5 py-3">Billing Cycle</th>
                                <th className="px-5 py-3">Regular Price</th>
                                <th className="px-5 py-3">Sale Price</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {subscriptionPlans.length === 0 ? (
                                <tr>
                                  <td colSpan={7} className="px-5 py-8 text-center text-sm text-gray-400 italic">
                                    No subscription plans configured yet. Click the button below to add your first plan.
                                  </td>
                                </tr>
                              ) : (
                                subscriptionPlans.map((plan, idx) => (
                                  <tr key={idx} className="text-xs text-gray-700 hover:bg-slate-50/50 transition-colors">
                                    <td className="px-5 py-3.5 font-medium text-gray-900">{plan.name}</td>
                                    <td className="px-5 py-3.5">
                                      <span className={`px-2 py-0.5 rounded font-semibold ${
                                        plan.tier === 'max' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'
                                      }`}>
                                        {plan.tier.toUpperCase()}
                                      </span>
                                    </td>
                                    <td className="px-5 py-3.5 capitalize">{plan.period}</td>
                                    <td className="px-5 py-3.5">৳{plan.regularPrice}</td>
                                    <td className="px-5 py-3.5">৳{plan.salePrice || plan.regularPrice}</td>
                                    <td className="px-5 py-3.5">
                                      <span className={`px-2 py-0.5 rounded font-semibold ${
                                        plan.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                                      }`}>
                                        {plan.status === 'active' ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td className="px-5 py-3.5 text-right space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => handleEditPlan(idx)}
                                        className="text-indigo-600 hover:text-indigo-900 font-bold bg-transparent border-none cursor-pointer"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeletePlan(idx)}
                                        className="text-rose-600 hover:text-rose-900 font-bold bg-transparent border-none cursor-pointer"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Add/Edit Form Panel */}
                        {showPlanForm ? (
                          <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-4">
                            <h4 className="text-sm font-bold text-slate-800">
                              {editingPlanIdx !== null ? "Edit Pricing Plan" : "Add New Pricing Plan"}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <Input
                                label="Plan Name"
                                placeholder="e.g. Pro Monthly Plan"
                                value={planForm.name}
                                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                                required
                              />
                              <Select
                                label="Subscription Tier"
                                options={[
                                  { value: "pro", label: "Pro Tier" },
                                  { value: "max", label: "Max Tier" },
                                ]}
                                value={planForm.tier}
                                onChange={(val) => setPlanForm({ ...planForm, tier: val })}
                              />
                              <Select
                                label="Billing Cycle"
                                options={[
                                  { value: "monthly", label: "Monthly" },
                                  { value: "yearly", label: "Yearly" },
                                  { value: "quarterly", label: "Quarterly" },
                                  { value: "one-time", label: "One-Time" },
                                ]}
                                value={planForm.period}
                                onChange={(val) => setPlanForm({ ...planForm, period: val })}
                              />
                              <Select
                                label="Status"
                                options={[
                                  { value: "active", label: "Active" },
                                  { value: "inactive", label: "Inactive" },
                                ]}
                                value={planForm.status}
                                onChange={(val) => setPlanForm({ ...planForm, status: val })}
                              />
                              <Input
                                label="Regular Price (৳)"
                                type="number"
                                placeholder="500"
                                value={planForm.regularPrice}
                                onChange={(e) => setPlanForm({ ...planForm, regularPrice: e.target.value })}
                                required
                              />
                              <Input
                                label="Sale Price (৳)"
                                type="number"
                                placeholder="350"
                                value={planForm.salePrice}
                                onChange={(e) => setPlanForm({ ...planForm, salePrice: e.target.value })}
                              />
                            </div>
                            <div className="flex gap-3 justify-end pt-2">
                              <Button
                                variant="outline"
                                type="button"
                                onClick={() => {
                                  setShowPlanForm(false);
                                  setEditingPlanIdx(null);
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                className="bg-[#00C9A7] hover:bg-[#00b898] text-white"
                                onClick={handleSavePlan}
                              >
                                Save Plan
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className="bg-indigo-600 hover:bg-indigo-700 text-white"
                              onClick={() => {
                                setPlanForm({
                                  name: "",
                                  tier: "pro",
                                  period: "monthly",
                                  regularPrice: "",
                                  salePrice: "",
                                  status: "active",
                                });
                                setEditingPlanIdx(null);
                                setShowPlanForm(true);
                              }}
                            >
                              + Add Pricing Plan
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Taxes sub-tab */}
                  {activeMonetizationSubTab === "taxes" && (
                    <Card
                      title="Tax Settings"
                      description="Configure global tax calculations for courses"
                    >
                      <div className="space-y-6">
                        <Toggle
                          label="Enable Tax"
                          description="Enable tax calculation at checkout"
                          checked={taxEnabled}
                          onChange={setTaxEnabled}
                        />
                        {taxEnabled && (
                          <>
                            <hr className="border-gray-100" />
                            <Input
                              label="Tax Rate (%)"
                              type="number"
                              placeholder="15"
                              value={taxRate}
                              onChange={(e) => setTaxRate(e.target.value)}
                            />
                            <Select
                              label="Tax Calculation Class"
                              options={[
                                { value: "exclusive", label: "Exclusive (Add tax on top of course price)" },
                                { value: "inclusive", label: "Inclusive (Tax is included in course price)" },
                              ]}
                              value={taxCalculation}
                              onChange={(val) => setTaxCalculation(val as any)}
                            />
                          </>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Checkout sub-tab */}
                  {activeMonetizationSubTab === "checkout" && (
                    <Card
                      title="Checkout Settings"
                      description="Customize redirect and Terms and Conditions pages"
                    >
                      <div className="space-y-6">
                        <Toggle
                          label="Redirect to Checkout Immediately"
                          description="Skip cart and redirect students directly to check out after clicking purchase"
                          checked={checkoutRedirect}
                          onChange={setCheckoutRedirect}
                        />
                        <hr className="border-gray-100" />
                        <Toggle
                          label="Enable Guest Checkout"
                          description="Allow users to purchase courses without registering an account beforehand"
                          checked={guestCheckout}
                          onChange={setGuestCheckout}
                        />
                        <hr className="border-gray-100" />
                        <Toggle
                          label="Enable Terms and Conditions"
                          description="Force users to agree to terms before completing checkout"
                          checked={enableTerms}
                          onChange={setEnableTerms}
                        />
                        {enableTerms && (
                          <Input
                            label="Terms and Conditions Page URL"
                            placeholder="/terms"
                            value={termsPageUrl}
                            onChange={(e) => setTermsPageUrl(e.target.value)}
                          />
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Referrals sub-tab */}
                  {activeMonetizationSubTab === "referrals" && (
                    <Card
                      title="Referral Program Settings"
                      description="Configure dynamic referral coupon codes and commission rates for students/instructors"
                    >
                      <div className="space-y-6">
                        <Toggle
                          label="Enable Referral Program"
                          description="Allow students/instructors to refer friends and earn commission on subscriptions"
                          checked={enableReferrals}
                          onChange={setEnableReferrals}
                        />
                        {enableReferrals && (
                          <>
                            <hr className="border-gray-100" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <Select
                                label="Referral Code Format"
                                value={referralCodeType}
                                onChange={(val) => setReferralCodeType(val as any)}
                                options={[
                                  { value: "username", label: "Username/Name-Based (e.g. AHMED10)" },
                                  { value: "random", label: "Random Unique Code (e.g. J2DL6)" },
                                ]}
                              />
                              <Select
                                label="Eligible Roles/Users"
                                value={referralEligibility}
                                onChange={(val) => setReferralEligibility(val as any)}
                                options={[
                                  { value: "global", label: "Global (All Registered Users)" },
                                  { value: "instructors", label: "Instructors Only" },
                                  { value: "specific", label: "Specific Users Only" },
                                ]}
                              />
                            </div>

                            {referralEligibility === "specific" && (
                              <Input
                                label="Specific User IDs / Usernames"
                                placeholder="Comma separated list, e.g. 101, 102, teacher_ali"
                                value={specificReferralUsers}
                                onChange={(e) => setSpecificReferralUsers(e.target.value)}
                              />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <Input
                                label="Referral Commission Rate (%)"
                                type="number"
                                placeholder="10"
                                value={referralCommissionRate}
                                onChange={(e) => setReferralCommissionRate(e.target.value)}
                              />
                              <Input
                                label="Referred User Discount (%)"
                                type="number"
                                placeholder="10"
                                value={referredDiscountRate}
                                onChange={(e) => setReferredDiscountRate(e.target.value)}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {/* ─── DESIGN ─── */}
              {activeTab === "design" && (
                <Card
                  title="Design Settings"
                  description="Customize platform appearance"
                >
                  <div className="space-y-6">
                    {/* Color Pickers */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[#374151] mb-2">
                          Primary Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            className="h-10 w-14 rounded-lg border border-gray-200 cursor-pointer"
                          />
                          <Input
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            containerClassName="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#374151] mb-2">
                          Secondary Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={secondaryColor}
                            onChange={(e) => setSecondaryColor(e.target.value)}
                            className="h-10 w-14 rounded-lg border border-gray-200 cursor-pointer"
                          />
                          <Input
                            value={secondaryColor}
                            onChange={(e) => setSecondaryColor(e.target.value)}
                            containerClassName="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#374151] mb-2">
                          Text Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={textColor}
                            onChange={(e) => setTextColor(e.target.value)}
                            className="h-10 w-14 rounded-lg border border-gray-200 cursor-pointer"
                          />
                          <Input
                            value={textColor}
                            onChange={(e) => setTextColor(e.target.value)}
                            containerClassName="flex-1"
                          />
                        </div>
                      </div>
                    </div>

                    <hr className="border-gray-100" />

                    {/* Logo Uploads */}
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-3">
                        Logo Uploads
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* LMS Admin Logo */}
                        {lmsAdminLogoUrl ? (
                          <div className="relative border border-gray-200 rounded-lg p-4 bg-gray-50/50 flex flex-col items-center justify-center min-h-[140px] group transition-all duration-200 hover:shadow-sm">
                            <img
                              src={lmsAdminLogoUrl}
                              alt="LMS Admin Logo"
                              className="max-h-12 object-contain"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => uploadLogo("lmsAdmin")}
                                className="px-3 py-1.5 bg-white text-xs font-semibold text-gray-800 rounded shadow hover:bg-gray-100 transition-colors"
                              >
                                Change
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setLmsAdminLogoUrl("");
                                  setLmsAdminLogoId("");
                                }}
                                className="px-3 py-1.5 bg-red-600 text-xs font-semibold text-white rounded shadow hover:bg-red-700 transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => uploadLogo("lmsAdmin")}
                            className="border-2 border-dashed border-gray-200 rounded-lg p-5 text-center hover:border-[#ff0000]/30 transition-colors cursor-pointer min-h-[140px] flex flex-col justify-center items-center"
                          >
                            <Upload className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm font-medium text-[#374151]">
                              LMS Admin Logo
                            </p>
                            <p className="text-xs text-[#5A6A85] mt-1">
                              Recommended 200x60px
                            </p>
                          </div>
                        )}

                        {/* User Dashboard Logo */}
                        {userDashboardLogoUrl ? (
                          <div className="relative border border-gray-200 rounded-lg p-4 bg-gray-50/50 flex flex-col items-center justify-center min-h-[140px] group transition-all duration-200 hover:shadow-sm">
                            <img
                              src={userDashboardLogoUrl}
                              alt="User Dashboard Logo"
                              className="max-h-12 object-contain"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => uploadLogo("userDashboard")}
                                className="px-3 py-1.5 bg-white text-xs font-semibold text-gray-800 rounded shadow hover:bg-gray-100 transition-colors"
                              >
                                Change
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setUserDashboardLogoUrl("");
                                  setUserDashboardLogoId("");
                                }}
                                className="px-3 py-1.5 bg-red-600 text-xs font-semibold text-white rounded shadow hover:bg-red-700 transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => uploadLogo("userDashboard")}
                            className="border-2 border-dashed border-gray-200 rounded-lg p-5 text-center hover:border-[#ff0000]/30 transition-colors cursor-pointer min-h-[140px] flex flex-col justify-center items-center"
                          >
                            <Upload className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm font-medium text-[#374151]">
                              User Dashboard Logo
                            </p>
                            <p className="text-xs text-[#5A6A85] mt-1">
                              Recommended 180x50px
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <hr className="border-gray-100" />

                    <Select
                      label="Typography Family"
                      options={[
                        { value: "inter", label: "Inter" },
                        { value: "system", label: "System UI" },
                        { value: "roboto", label: "Roboto" },
                        { value: "opensans", label: "Open Sans" },
                      ]}
                      value={typographyFamily}
                      onChange={setTypographyFamily}
                    />

                    <hr className="border-gray-100" />

                    {/* Page Features */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-[#1f2937]">Page Features</h4>
                        <p className="text-xs text-gray-400 mt-1">
                          You can keep the following features active or inactive as per the need of your business model
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                        <Toggle
                          label="Instructor Info"
                          checked={pageFeatureInstructorInfo}
                          onChange={setPageFeatureInstructorInfo}
                        />
                        <Toggle
                          label="Wishlist"
                          checked={pageFeatureWishlist}
                          onChange={setPageFeatureWishlist}
                        />
                        <Toggle
                          label="Q&A"
                          checked={pageFeatureQA}
                          onChange={setPageFeatureQA}
                        />
                        <Toggle
                          label="Author"
                          checked={pageFeatureAuthor}
                          onChange={setPageFeatureAuthor}
                        />
                        <Toggle
                          label="Level"
                          checked={pageFeatureLevel}
                          onChange={setPageFeatureLevel}
                        />
                        <Toggle
                          label="Social Share"
                          checked={pageFeatureSocialShare}
                          onChange={setPageFeatureSocialShare}
                        />
                        <Toggle
                          label="Duration"
                          checked={pageFeatureDuration}
                          onChange={setPageFeatureDuration}
                        />
                        <Toggle
                          label="Total Enrolled"
                          checked={pageFeatureTotalEnrolled}
                          onChange={setPageFeatureTotalEnrolled}
                        />
                        <Toggle
                          label="Update Date"
                          checked={pageFeatureUpdateDate}
                          onChange={setPageFeatureUpdateDate}
                        />
                        <Toggle
                          label="Progress Bar"
                          checked={pageFeatureProgressBar}
                          onChange={setPageFeatureProgressBar}
                        />
                        <Toggle
                          label="Material"
                          checked={pageFeatureMaterial}
                          onChange={setPageFeatureMaterial}
                        />
                        <Toggle
                          label="About"
                          checked={pageFeatureAbout}
                          onChange={setPageFeatureAbout}
                        />
                        <Toggle
                          label="Description"
                          checked={pageFeatureDescription}
                          onChange={setPageFeatureDescription}
                        />
                        <Toggle
                          label="Benefits"
                          checked={pageFeatureBenefits}
                          onChange={setPageFeatureBenefits}
                        />
                        <Toggle
                          label="Requirements"
                          checked={pageFeatureRequirements}
                          onChange={setPageFeatureRequirements}
                        />
                        <Toggle
                          label="Target Audience"
                          checked={pageFeatureTargetAudience}
                          onChange={setPageFeatureTargetAudience}
                        />
                        <Toggle
                          label="Announcements"
                          checked={pageFeatureAnnouncements}
                          onChange={setPageFeatureAnnouncements}
                        />
                        <Toggle
                          label="Review"
                          checked={pageFeatureReview}
                          onChange={setPageFeatureReview}
                        />
                        <Toggle
                          label="Sticky Sidebar"
                          checked={pageFeatureStickySidebar}
                          onChange={setPageFeatureStickySidebar}
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* ─── ZOOM ─── */}
              {activeTab === "zoom" && (
                <div className="space-y-6">
                  {/* Connection Status Banner */}
                  <div className={`flex items-center gap-4 p-4 rounded-xl border ${
                    zoomConnected
                      ? "bg-emerald-50/60 border-emerald-200"
                      : "bg-amber-50/60 border-amber-200"
                  }`}>
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                      zoomConnected ? "bg-emerald-100" : "bg-amber-100"
                    }`}>
                      {zoomConnected ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-semibold ${
                        zoomConnected ? "text-emerald-800" : "text-amber-800"
                      }`}>
                        {zoomConnected ? "Zoom Connected" : "Zoom Not Connected"}
                      </p>
                      <p className={`text-xs mt-0.5 ${
                        zoomConnected ? "text-emerald-600" : "text-amber-600"
                      }`}>
                        {zoomConnected
                          ? "Your Zoom account is linked and ready for live classes."
                          : "Enter your API credentials below to connect your Zoom account."}
                      </p>
                    </div>
                    <Button
                      variant={zoomConnected ? "outline" : "primary"}
                      size="sm"
                      onClick={() => setZoomConnected(!zoomConnected)}
                      leftIcon={<Link2 className="h-3.5 w-3.5" />}
                    >
                      {zoomConnected ? "Disconnect" : "Connect"}
                    </Button>
                  </div>

                  {/* API Credentials Card */}
                  <Card
                    title="API Credentials"
                    description="Connect your Zoom account for live class integration"
                  >
                    <div className="space-y-6">
                      <Toggle
                        label="Enable Zoom Integration"
                        description="Allow instructors to create and manage Zoom meetings for courses"
                        checked={zoomEnabled}
                        onChange={setZoomEnabled}
                      />

                      {zoomEnabled && (
                        <>
                          <hr className="border-gray-100" />

                          {/* Auth Type Selector */}
                          <div>
                            <div className="block text-sm font-medium text-[#374151] mb-3">
                              Authentication Method
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div
                                onClick={() => setZoomAuthType("jwt")}
                                className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                                  zoomAuthType === "jwt"
                                    ? "bg-red-50/40 border-[#ff0000]/30 shadow-sm"
                                    : "border-gray-200 bg-white hover:border-[#ff0000]/20"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="zoomAuth"
                                  checked={zoomAuthType === "jwt"}
                                  onChange={() => setZoomAuthType("jwt")}
                                  className="text-[#ff0000]"
                                />
                                <div>
                                  <p className="text-sm font-medium text-gray-800">Server-to-Server OAuth</p>
                                  <p className="text-xs text-[#5A6A85] mt-0.5">Recommended for most setups</p>
                                </div>
                              </div>
                              <div
                                onClick={() => setZoomAuthType("oauth")}
                                className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                                  zoomAuthType === "oauth"
                                    ? "bg-red-50/40 border-[#ff0000]/30 shadow-sm"
                                    : "border-gray-200 bg-white hover:border-[#ff0000]/20"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="zoomAuth"
                                  checked={zoomAuthType === "oauth"}
                                  onChange={() => setZoomAuthType("oauth")}
                                  className="text-[#ff0000]"
                                />
                                <div>
                                  <p className="text-sm font-medium text-gray-800">OAuth 2.0</p>
                                  <p className="text-xs text-[#5A6A85] mt-0.5">For marketplace integrations</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input
                              label={zoomAuthType === "jwt" ? "Account ID" : "Client ID"}
                              placeholder={zoomAuthType === "jwt" ? "Enter Zoom Account ID" : "Enter OAuth Client ID"}
                              leftIcon={<Shield className="h-4 w-4" />}
                              value={zoomClientId}
                              onChange={(e) => setZoomClientId(e.target.value)}
                            />
                            <Input
                              label={zoomAuthType === "jwt" ? "Client ID" : "Client Secret"}
                              placeholder={zoomAuthType === "jwt" ? "Enter Zoom Client ID" : "Enter OAuth Client Secret"}
                              type="password"
                              leftIcon={<Shield className="h-4 w-4" />}
                              value={zoomClientSecret}
                              onChange={(e) => setZoomClientSecret(e.target.value)}
                            />
                          </div>

                          <Input
                            label={zoomAuthType === "jwt" ? "Client Secret" : "Redirect URI"}
                            placeholder={zoomAuthType === "jwt" ? "Enter Zoom Client Secret" : "https://yourdomain.com/zoom/callback"}
                            type={zoomAuthType === "jwt" ? "password" : "text"}
                            leftIcon={<Shield className="h-4 w-4" />}
                            value={zoomApiSecret}
                            onChange={(e) => setZoomApiSecret(e.target.value)}
                          />

                          <Input
                            label="Zoom Account Email"
                            placeholder="admin@yourdomain.com"
                            type="email"
                            helper="The email address associated with your Zoom account"
                            value={zoomAccountEmail}
                            onChange={(e) => setZoomAccountEmail(e.target.value)}
                          />
                        </>
                      )}
                    </div>
                  </Card>

                  {/* Meeting Defaults Card */}
                  {zoomEnabled && (
                    <Card
                      title="Meeting Defaults"
                      description="Default settings for new Zoom meetings"
                    >
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <Select
                            label="Default Duration (minutes)"
                            options={[
                              { value: "30", label: "30 minutes" },
                              { value: "45", label: "45 minutes" },
                              { value: "60", label: "60 minutes" },
                              { value: "90", label: "90 minutes" },
                              { value: "120", label: "120 minutes" },
                            ]}
                            value={zoomDefaultDuration}
                            onChange={setZoomDefaultDuration}
                          />
                          <Select
                            label="Default Timezone"
                            options={[
                              { value: "Asia/Dhaka", label: "Asia/Dhaka (UTC+6)" },
                              { value: "Asia/Kolkata", label: "Asia/Kolkata (UTC+5:30)" },
                              { value: "America/New_York", label: "America/New York (EST)" },
                              { value: "Europe/London", label: "Europe/London (GMT)" },
                              { value: "Asia/Tokyo", label: "Asia/Tokyo (JST)" },
                            ]}
                            value={zoomDefaultTimezone}
                            onChange={setZoomDefaultTimezone}
                          />
                        </div>

                        <hr className="border-gray-100" />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <Toggle
                            label="Auto-Record Meetings"
                            description="Automatically record all live class sessions to the cloud"
                            checked={zoomAutoRecord}
                            onChange={setZoomAutoRecord}
                          />
                          <Toggle
                            label="Enable Waiting Room"
                            description="Students must be admitted by the host before joining"
                            checked={zoomWaitingRoom}
                            onChange={setZoomWaitingRoom}
                          />
                        </div>

                        <hr className="border-gray-100" />

                        <Select
                          label="Recording Storage"
                          options={[
                            { value: "cloud", label: "Zoom Cloud" },
                            { value: "local", label: "Local Server" },
                            { value: "both", label: "Cloud + Local Backup" },
                          ]}
                          value={zoomRecordingStorage}
                          onChange={setZoomRecordingStorage}
                        />
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {/* ─── AUTHENTICATION ─── */}
              {activeTab === "authentication" && (
                <div className="space-y-6">
                  {/* Two-Factor Authentication */}
                  <Card
                    title="Two-Factor Authentication"
                    description="Secure student and instructor accounts with mobile OTP verification"
                  >
                    <Toggle
                      label="Enable 2FA"
                      description="Toggle to require a GreenWeb SMS OTP verification code upon successful login"
                      checked={enable2fa}
                      onChange={setEnable2fa}
                    />
                  </Card>

                  {/* Fraud Protection */}
                  <Card
                    title="Fraud Protection"
                    description="Prevent unauthorized login attempts and malicious user registrations"
                  >
                    <Toggle
                      label="Enable Fraud Protection"
                      description="Toggle to enable security checking and fraud verification controls"
                      checked={enableFraudProtection}
                      onChange={setEnableFraudProtection}
                    />
                  </Card>

                  {/* Limit Active Login Sessions */}
                  <Card
                    title="Manage Active Login Sessions"
                    description="Restrict concurrent logins for individual student and instructor accounts"
                  >
                    <Toggle
                      label="Limit Active Login Sessions"
                      description="Toggle to limit the number of active sessions for a concurrent user login."
                      checked={limitActiveLoginSessions}
                      onChange={setLimitActiveLoginSessions}
                    />
                  </Card>

                  {/* Email Verification */}
                  <Card
                    title="Email Verification"
                    description="Verify identity and authenticity of registration emails"
                  >
                    <Toggle
                      label="Enable Email Verification"
                      description="Toggle to enable email verification for students and instructor signup"
                      checked={enableEmailVerification}
                      onChange={setEnableEmailVerification}
                    />
                  </Card>

                  {/* Social Login */}
                  <Card
                    title="Social Login"
                    description="Allow users to log in instantly using their social media profiles"
                  >
                    <div className="space-y-4">
                      <Toggle
                        label="Google"
                        description="Enable Google Login"
                        checked={enableGoogleLogin}
                        onChange={setEnableGoogleLogin}
                      />
                      <hr className="border-gray-100" />
                      <Toggle
                        label="Facebook"
                        description="Enable Facebook Login"
                        checked={enableFacebookLogin}
                        onChange={setEnableFacebookLogin}
                      />
                      <hr className="border-gray-100" />
                      <Toggle
                        label="Twitter"
                        description="Enable Twitter Login"
                        checked={enableTwitterLogin}
                        onChange={setEnableTwitterLogin}
                      />
                    </div>
                  </Card>

                  {/* SMS Gateway Settings */}
                  <Card
                    title="SMS Gateway Configuration"
                    description="Configure GreenWeb SMS gateway credentials and OTP templates"
                  >
                    <div className="space-y-4 text-left">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-gray-500 mb-1">
                          GreenWeb API Token
                        </label>
                        <Input
                          placeholder="Token-xxxxxxxxx"
                          value={greenwebApiToken}
                          onChange={(e) => setGreenwebApiToken(e.target.value)}
                        />
                        <p className="text-[10px] text-gray-400 mt-1">
                          GreenWeb SMS প্যানেল থেকে প্রাপ্ত API Token দিন।
                        </p>
                      </div>
                      <hr className="border-gray-100" />
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-gray-500 mb-1">
                          OTP SMS Template
                        </label>
                        <textarea
                          placeholder="Your Shuddhoshikha verification code is {otp}."
                          value={otpMessageTemplate}
                          onChange={(e) => setOtpMessageTemplate(e.target.value)}
                          className="rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-xs text-gray-700 font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20 h-24 w-full"
                        />
                        <p className="text-[10px] text-gray-400 mt-1">
                          ওটিপি বার্তার টেমপ্লেট। ওটিপি বসানোর স্থানে অবশ্যই <span className="font-bold text-indigo-500">{"{otp}"}</span> ট্যাগটি ব্যবহার করতে হবে।
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Academic Class & Batch Settings */}
                  <Card
                    title="Academic Class & Batch Settings"
                    description="Manage available class and batch selections for students profile"
                  >
                    <div className="space-y-4 text-left">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-gray-500 mb-1">
                          ডায়নামিক ক্লাসসমূহ
                        </label>
                        <Input
                          placeholder="Class 6, Class 7, Class 8..."
                          value={dynamicClasses}
                          onChange={(e) => setDynamicClasses(e.target.value)}
                        />
                        <p className="text-[10px] text-gray-400 mt-1">
                          কমা দিয়ে আলাদা করা ক্লাসের তালিকা (যেমন: Class 6, Class 7, Class 8)।
                        </p>
                      </div>
                      <hr className="border-gray-100" />
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-gray-500 mb-1">
                          এসএসসি (SSC) ব্যাচসমূহ
                        </label>
                        <Input
                          placeholder="SSC 2027, SSC 2028..."
                          value={sscBatches}
                          onChange={(e) => setSscBatches(e.target.value)}
                        />
                        <p className="text-[10px] text-gray-400 mt-1">
                          কমা দিয়ে আলাদা করা এসএসসি ব্যাচ (যেমন: SSC 2027, SSC 2028)।
                        </p>
                      </div>
                      <hr className="border-gray-100" />
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-gray-500 mb-1">
                          এইচএসসি (HSC) ব্যাচসমূহ
                        </label>
                        <Input
                          placeholder="HSC 2026, HSC 2027..."
                          value={hscBatches}
                          onChange={(e) => setHscBatches(e.target.value)}
                        />
                        <p className="text-[10px] text-gray-400 mt-1">
                          কমা দিয়ে আলাদা করা এইচএসসি ব্যাচ (যেমন: HSC 2026, HSC 2027)।
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* ─── AVATAR GALLERY PRESETS ─── */}
              {activeTab === "avatars" && (
                <Card
                  title="Avatar Presets Gallery"
                  description="Upload preset profile pictures that students can choose from"
                >
                  <div className="space-y-6">
                    {/* Instructions */}
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
                      <p className="font-semibold mb-1">How it works:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Upload multiple avatar images. Supports PNG, JPEG, GIF.</li>
                        <li>Mark one avatar as default. Users without custom avatars will get this.</li>
                        <li>Click "Save Settings" at the top right to persist changes.</li>
                      </ul>
                    </div>

                    {/* Upload button */}
                    <div>
                      <Button
                        leftIcon={<Upload className="h-4 w-4" />}
                        onClick={() => {
                          const wp = (window as any).wp;
                          if (wp && wp.media) {
                            const frame = wp.media({
                              title: "Select or Upload Preset Avatars",
                              button: {
                                text: "Add to Gallery",
                              },
                              library: {
                                type: "image",
                              },
                              multiple: true,
                            });

                            frame.on("select", () => {
                              const attachments = frame.state().get("selection").toJSON();
                              const newPresets = attachments.map((att: any) => ({
                                id: att.id,
                                url: att.url,
                                is_default: false,
                              }));
                              setAvatarGallery((prev) => {
                                // Prevent duplicates
                                const filtered = newPresets.filter(
                                  (np: any) => !prev.some((p) => p.url === np.url)
                                );
                                return [...prev, ...filtered];
                              });
                            });

                            frame.open();
                          } else {
                            alert("WordPress Media Library is not loaded.");
                          }
                        }}
                      >
                        Upload Preset Avatars
                      </Button>
                    </div>

                    {/* Presets Grid */}
                    {avatarGallery.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-xl">
                        No preset avatars uploaded yet.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4">
                        {avatarGallery.map((av) => (
                          <div
                            key={av.id || av.url}
                            className={`group relative rounded-2xl overflow-hidden border-2 aspect-square flex items-center justify-center bg-gray-50 transition-all ${
                              av.is_default ? "border-amber-500 shadow-md ring-2 ring-amber-100" : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <img
                              src={av.url}
                              alt="Avatar Preset"
                              className="w-full h-full object-cover"
                            />
                            
                            {/* Hover Actions Overlay */}
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                              {/* Star / Default Toggle */}
                              <button
                                type="button"
                                onClick={() => {
                                  setAvatarGallery((prev) =>
                                    prev.map((p) => ({
                                      ...p,
                                      is_default: p.url === av.url,
                                    }))
                                  );
                                }}
                                className={`p-1.5 rounded-full transition-colors ${
                                  av.is_default
                                    ? "bg-amber-500 text-white"
                                    : "bg-white/80 hover:bg-white text-gray-700"
                                }`}
                                title="Set as default avatar"
                              >
                                <Star className="h-4 w-4 fill-current" />
                              </button>

                              {/* Remove button */}
                              <button
                                type="button"
                                onClick={() => {
                                  setAvatarGallery((prev) =>
                                    prev.filter((p) => p.url !== av.url)
                                  );
                                }}
                                className="p-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white transition-colors"
                                title="Remove avatar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            {/* Default Badge */}
                            {av.is_default && (
                              <div className="absolute top-2 left-2 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                                Default
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── MANUAL PAYMENT MODAL ─── */}
      {showManualModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 flex flex-col relative animate-slideIn">
            <button
              type="button"
              onClick={() => setShowManualModal(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-lg font-bold text-gray-800 mb-1">
              Add Manual Payment Method
            </h3>
            <p className="text-xs text-[#5A6A85] mb-5">
              Create a custom offline payment option for students
            </p>

            <div className="space-y-4 flex-1">
              <Input
                label="Payment Method Title"
                placeholder="e.g. Bank Transfer, bKash Personal, Cash on Delivery"
                value={manualTitle}
                onChange={(e) => setManualTitle(e.target.value)}
              />

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Payment Method Icon (Optional)
                </label>
                <div className="flex items-center gap-3">
                  {manualIconUrl ? (
                    <div className="relative h-12 w-12 rounded border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0 group">
                      <img src={manualIconUrl} alt="Preview" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setManualIconUrl("")}
                        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-semibold"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="h-12 w-12 rounded border-2 border-dashed border-gray-200 flex items-center justify-center shrink-0">
                      <Upload className="h-4 w-4 text-gray-300" />
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const wp = (window as any).wp;
                      if (wp && wp.media) {
                        const frame = wp.media({
                          title: "Select or Upload Payment Icon",
                          button: { text: "Use this icon" },
                          multiple: false,
                        });
                        frame.on("select", () => {
                          const attachment = frame.state().get("selection").first().toJSON();
                          setManualIconUrl(attachment.url);
                        });
                        frame.open();
                      } else {
                        alert("WordPress Media Library is not loaded.");
                      }
                    }}
                  >
                    Select Icon
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">
                  Payment Instructions
                </label>
                <textarea
                  className="w-full min-h-[100px] p-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#ff0000] focus:ring-1 focus:ring-[#ff0000]"
                  placeholder="Provide details on how users should make the payment (e.g. Bank details, Phone numbers, Reference instructions)"
                  value={manualInstructions}
                  onChange={(e) => setManualInstructions(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-150">
              <Button
                variant="outline"
                onClick={() => setShowManualModal(false)}
              >
                Cancel
              </Button>
              <Button
                disabled={!manualTitle.trim()}
                onClick={() => {
                  const newMethod = {
                    id: `manual_${Date.now()}`,
                    title: manualTitle.trim(),
                    iconUrl: manualIconUrl,
                    instructions: manualInstructions.trim(),
                    enabled: true,
                  };
                  setManualPayments([...manualPayments, newMethod]);
                  setShowManualModal(false);
                  showToast("Manual payment method added. Don't forget to Save Settings!", "success");
                }}
              >
                Add Method
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
